"use client"
import { cn, currencyHelper } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import {
    Calendar,
    Check,
    Clock,
    Loader2,
    MapPin,
    MinusCircle,
    PlusCircle,
    User2,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { PhoneInput } from "@/components/ui/phone-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type RouterOutputs } from "@/trpc/react";
import { type AddonType } from "@/types/trip.type";
import { useSession } from "../auth/session-provider";
import { toast } from "sonner";
import { bookEnquiry } from "@/server/actions/google-sheets-actions";
import DateGroupSliderBooking from "../global/group-date-booking";
import { DESTINATION_BG } from "@/lib/constant";
import { ImageLoader } from "../ui/image-loader";

interface BookingFormProps {
    data: RouterOutputs["trips"]["get_for_booking"];
    tripId: string;
    img: string | null;
}

export default function BookingEnquiryForm({ data, img }: BookingFormProps) {
    const { addons, number_of_days, number_of_nights, title, trip_batches } = data!
    const ParsedAddons: AddonType[] = JSON.parse(JSON.stringify(addons)) as AddonType[]
    const [isPending, startTransaction] = useTransition();
    const { user } = useSession();
    const router = useRouter();
    const [phone, setPhone] = useState<string | null | undefined>(user?.phone)
    const [name, setName] = useState<string | undefined>(user?.fullname)
    const [email, setEmail] = useState<string | undefined>(user?.email)


    // State variables
    const [selectedCity, setSelectedCity] = useState<string>(
        trip_batches[0]?.city ?? ""
    );

    const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined)

    const [selectedDate, setSelectedDate] = useState<{
        id: string;
        start_date: Date;
        end_date: Date;
    } | null>(null);
    const [selectedDatePrice, setSelectedDatePrice] = useState<number | null>(
        null
    );
    const [selectedTransportOptions, setSelectedTransportOptions] = useState<
        {
            id: string;
            transport: string;
            price_per_person: number;
            people: number;
            max_capacity: number;
        }[]
    >([]);
    const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>({});

    // Handle city change
    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newCity = event.target.value;
        setSelectedCity(newCity);
        setSelectedDate(null);
        setSelectedDatePrice(null);
        setSelectedLocation(undefined);
        setSelectedTransportOptions(
            trip_batches
                .find((batch) => batch.city === newCity)?.available_dates.find((d) => d.id === selectedDate?.id)?.mode_of_transports?.map((mode) => ({
                    id: mode.id,
                    transport: mode.transport,
                    price_per_person: mode.price_per_person,
                    people: 0,
                    max_capacity: mode.max_capacity,
                })) ?? []
        );
        setSelectedAddons({});
    };

    // Handle date change
    const handleDateChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        dateData: { id: string, start_date: Date; end_date: Date; price: number }
    ) => {
        setSelectedDate({
            id: dateData.id,
            start_date: dateData.start_date,
            end_date: dateData.end_date,
        });
        setSelectedDatePrice(dateData.price);
    };

    // Update people count for transport options
    const handleTransportPeopleChange = (
        action: "increment" | "decrement",
        transport: {
            id: string;
            transport: string;
            price_per_person: number;
            max_capacity: number;
        }
    ) => {
        setSelectedTransportOptions((prevOptions) => {
            const existingOption = prevOptions.find((option) => option.id === transport.id);

            // Determine new people count
            let newPeopleCount = existingOption?.people ?? 0;
            if (action === "increment") {
                newPeopleCount++;
            } else if (action === "decrement" && newPeopleCount > 0) {
                newPeopleCount--;
            }

            // Ensure count is within valid range
            if (newPeopleCount < 0 || newPeopleCount > transport.max_capacity) {
                return prevOptions;
            }

            // Update or add the transport option
            if (existingOption) {
                return prevOptions.map((option) =>
                    option.id === transport.id ? { ...option, people: newPeopleCount } : option
                );
            } else {
                return [
                    ...prevOptions,
                    { ...transport, people: newPeopleCount },
                ];
            }
        });
    };

    // Update count for add-ons
    const handleAddonChange = (
        action: "increment" | "decrement",
        addon: AddonType
    ) => {
        setSelectedAddons((prevAddons) => {
            const currentCount = prevAddons[addon.title] ?? 0;
            if (action === "increment") {
                return { ...prevAddons, [addon.title]: currentCount + 1 };
            } else if (currentCount > 0) {
                return { ...prevAddons, [addon.title]: currentCount - 1 };
            }
            return prevAddons;
        });
    };

    // Get available dates for the selected city
    const getAvailableDates = () => {
        const selectedBatch = trip_batches.find(
            (batch) => batch.city === selectedCity
        );
        return selectedBatch ? selectedBatch.available_dates : [];
    };

    const availableDates = getAvailableDates();

    // Calculate the total number of people from transport options
    const totalPeopleForTransport = selectedTransportOptions.reduce(
        (sum, option) => sum + option.people,
        0
    );

    // Calculate the total price
    const transportCost = selectedTransportOptions.reduce(
        (sum, option) => sum + option.price_per_person * option.people,
        0
    );
    const dateCost = selectedDatePrice
        ? selectedDatePrice * totalPeopleForTransport
        : 0;
    const addonCost = Object.keys(selectedAddons).reduce((sum, key) => {
        const addon: AddonType | undefined = Array.isArray(addons) ? (addons as AddonType[]).find((a: AddonType) => a.title === key) : undefined;
        return (
            sum + (addon ? parseInt(addon.price) * (selectedAddons[key] ?? 0) : 0)
        );
    }, 0);

    const totalPrice = transportCost + dateCost + addonCost;
    // No discount in enquiry form, but maintain consistent calculation
    const subtotalAfterDiscount = totalPrice;
    const gst_amount = Number((subtotalAfterDiscount * 0.05).toFixed(2));
    const grandTotal = Number((subtotalAfterDiscount + gst_amount).toFixed(2));

    // Handle booking submission
    const handleBooking = () => {
        if (totalPeopleForTransport > 0) {
            startTransaction(async () => {
                if (!phone || !name || !email) {
                    toast.error('Please fill all the required fields');
                    return;
                }

                const addonDetails = Object.entries(selectedAddons)
                    .filter(([, count]) => count > 0)
                    .map(([addonTitle, count]) => ({
                        addon_title: addonTitle,
                        addon_price: ParsedAddons.find((a) => a.title === addonTitle)?.price,
                        addon_unit: count,
                    }));

                // Convert the array of addon details to JSON without pretty-printing
                const addonDetailsJSON = JSON.stringify(addonDetails);

                const response = await bookEnquiry({
                    city: selectedCity,
                    start_date: selectedDate ? selectedDate.start_date : new Date(),
                    end_date: selectedDate ? selectedDate.end_date : new Date(),
                    total_amount: totalPrice,
                    total_members: totalPeopleForTransport,
                    transport_details: selectedTransportOptions.map(
                        (options) => ({
                            number_of_travelors: options.people,
                            transport_optionns: options.transport,
                            transport_price:
                                selectedDatePrice ?? 0 + options.price_per_person,
                        })
                    ),
                    addon_details: addonDetailsJSON,
                    gst: gst_amount,
                    grand_total: grandTotal,
                    trip_title: title,
                    pickup_location: selectedLocation,
                    name,
                    email,
                    phone
                })

                if (!response.success) {
                    toast.error(response.message)
                    return
                }

                toast.success(response.message)

                router.push('/')
            })
        }
    };

    const [currentForm, setCurrentForm] = useState<number>(0);

    return (
        <>
            <div className="grid grid-cols-4 gap-3 py-6 text-sm leading-tight md:text-base">
                <div className="flex flex-col gap-1">
                    <p>Select Batch</p>
                    <span className="h-2.5 w-full bg-zinc-200 relative">
                        <span
                            className={cn(
                                "h-full w-full absolute top-0 left-0 bg-custom-secondary origin-left transition duration-500 delay-75",
                                currentForm >= 0 ? "scale-x-100" : "scale-x-0"
                            )}
                        />
                    </span>
                </div>
                <div className="flex flex-col gap-1">
                    <p>No. Of Members</p>
                    <span className="h-2.5 w-full bg-zinc-200 relative">
                        <span
                            className={cn(
                                "h-full w-full absolute top-0 left-0 bg-custom-secondary origin-left transition duration-500 delay-75 scale-x-0",
                                currentForm >= 1 ? "scale-x-100" : "scale-x-0"
                            )}
                        />
                    </span>
                </div>
                <div className="flex flex-col gap-1">
                    <p>Personal Details</p>
                    <span className="h-2.5 w-full bg-zinc-200 relative">
                        <span
                            className={cn(
                                "h-full w-full absolute top-0 left-0 bg-custom-secondary origin-left transition duration-500 delay-75 scale-x-0",
                                currentForm >= 2 ? "scale-x-100" : "scale-x-0"
                            )}
                        />
                    </span>
                </div>
                <div className="flex flex-col gap-1">
                    <p>Booking Status</p>
                    <span className="h-2.5 w-full bg-zinc-200 relative">
                        <span
                            className={cn(
                                "h-full w-full absolute top-0 left-0 bg-custom-secondary origin-left transition duration-500 delay-75 scale-x-0",
                                currentForm >= 3 ? "scale-x-100" : "scale-x-0"
                            )}
                        />
                    </span>
                </div>
            </div>
            <Card className="w-full overflow-hidden border-zinc-400">
                <CardHeader className="font-bold text-center text-lg p-3 font-lexend bg-custom-primary-shade border-b border-b-zinc-400">
                    <div
                        className="h-full w-full flex flex-row flex-nowrap transition duration-500 delay-75"
                        style={{ transform: `translateX(-${currentForm * 100}%)` }}
                    >
                        <p className="w-full shrink-0 text-center">
                            Select Boarding City and Date
                        </p>
                        <p className="w-full shrink-0 text-center">
                            Select Number Of Members
                        </p>
                        <p className="w-full shrink-0 text-center">Personal Details</p>
                        <p className="w-full shrink-0 text-center">
                            Booking Completed Sucessfully
                        </p>
                    </div>
                </CardHeader>
                <CardContent className="w-full overflow-hidden p-0">
                    <div
                        className="h-full w-full flex flex-row flex-nowrap transition duration-500 delay-75"
                        style={{ transform: `translateX(-${currentForm * 100}%)` }}
                    >
                        <div className="w-full shrink-0 p-6 space-y-6">
                            {/* City Selection */}
                            <div>
                                <h3 className="font-bold mb-2">Select a City:</h3>
                                <div className="flex flex-row flex-wrap gap-2.5">
                                    {trip_batches.map((batch) => (
                                        <div key={batch.city} className="flex w-fit">
                                            <input
                                                type="radio"
                                                id={batch.city}
                                                value={batch.city}
                                                checked={selectedCity === batch.city}
                                                onChange={handleCityChange}
                                                className="mr-2 sr-only"
                                            />
                                            <label
                                                htmlFor={batch.city}
                                                className="flex items-center px-2.5 py-1 border border-zinc-300 rounded bg-custom-primary-shade w-fit cursor-pointer"
                                            >
                                                <div
                                                    className={cn(
                                                        "h-4 w-4 rounded-full mr-2",
                                                        selectedCity === batch.city
                                                            ? "bg-green-400"
                                                            : "bg-white"
                                                    )}
                                                >
                                                    {selectedCity === batch.city && (
                                                        <Check className="h-full w-full text-white scale-[0.88]" />
                                                    )}
                                                </div>
                                                <p>{batch.city}</p>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>


                            <div>
                                <h3 className="font-bold mb-2">Select Pickup Location:</h3>
                                <div className="flex flex-row flex-wrap gap-2.5">
                                    {trip_batches.find(b => b.city === selectedCity)?.pickup_location ? trip_batches.find(b => b.city === selectedCity)?.pickup_location?.split(', ').map((location) => (
                                        <div key={location} className="flex w-fit">
                                            <input
                                                type="radio"
                                                id={location}
                                                value={location}
                                                checked={selectedLocation === location}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                                className="mr-2 sr-only"
                                            />
                                            <label
                                                htmlFor={location}
                                                className="flex items-center px-2.5 py-1 border border-zinc-300 rounded bg-custom-primary-shade w-fit cursor-pointer"
                                            >
                                                <div
                                                    className={cn(
                                                        "h-4 w-4 rounded-full mr-2",
                                                        selectedLocation === location
                                                            ? "bg-green-400"
                                                            : "bg-white"
                                                    )}
                                                >
                                                    {selectedLocation === location && (
                                                        <Check className="h-full w-full text-white scale-[0.88]" />
                                                    )}
                                                </div>
                                                <p>{location}</p>
                                            </label>
                                        </div>
                                    ))
                                        :
                                        <p>No Pickup location available.</p>
                                    }
                                </div>
                            </div>

                            {/* Date Selection */}
                            <div className="w-full shrink-0">
                                <h3 className="font-bold mb-2">
                                    Available Dates for {selectedCity}:
                                </h3>
                                <DateGroupSliderBooking
                                    availableDates={availableDates}
                                    selectedDate={selectedDate}
                                    setSelectedDate={setSelectedDate}
                                    handleDateChange={handleDateChange}
                                />
                            </div>
                            <div className="flex justify-end">
                                <button
                                    disabled={selectedDate === null}
                                    onClick={() => setCurrentForm(currentForm + 1)}
                                    className="disabled:cursor-not-allowed disabled:opacity-55 primary-btn bg-custom-primary"
                                >
                                    Next Step
                                </button>
                            </div>
                        </div>

                        {/* Transport Options */}
                        <div className="w-full shrink-0 p-6 md:p-8 lg:p-10 lg:pb-4 space-y-6">
                            <div className="flex flex-col md:flex-row flex-nowrap gap-6">
                                <div className="relative h-28 w-40">
                                    <ImageLoader
                                        src={img ?? DESTINATION_BG}
                                        alt="Trek Karo India"
                                        fill
                                        className="object-cover rounded-lg"
                                    />
                                </div>
                                <div className="space-y-4">
                                    <h3 className="font-lexend font-semibold text-2xl md:text-3xl text-primary-text">
                                        {title}
                                    </h3>
                                    <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x">
                                        <div className="flex md:flex-col">
                                            <div className="flex flex-nowrap items-center gap-2 mb-1">
                                                <MapPin className="h-4 w-4" />
                                                {selectedCity} to {selectedCity}
                                            </div>
                                            <div className="flex flex-nowrap items-center gap-2">
                                                <Clock className="h-4 w-4" />
                                                {number_of_days} Days / {number_of_nights}{" "}
                                                Nights
                                            </div>
                                        </div>
                                        <div className="flex flex-nowrap items-center gap-2 pt-1 mt-1 md:mt-0 md:pt-0 md:pl-6 md:ml-6">
                                            <Calendar className="h-4 w-4" />
                                            {selectedDate?.start_date.toDateString()}{" "}
                                            <br className="hidden md:block" />
                                            To {selectedDate?.end_date.toDateString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="border border-secondary-text/40 rounded-lg relative overflow-hidden">
                                <div className="py-2 px-4 md:px-8 text-sm md:text-base grid grid-cols-3 bg-custom-primary-shade w-full">
                                    <p>Transport Options</p>
                                    <p className="text-center">Price(per person)</p>
                                    <div className="flex items-center justify-end">
                                        <User2 className="size-5 md:size-6" />
                                    </div>
                                </div>
                                <div className="p-3 md:p-4 space-y-5 text-sm md:text-base">
                                    {(() => {
                                        const batch = trip_batches.find((batch) => batch.city === selectedCity);
                                        if (!batch) {
                                            return <p>No transport options available from {selectedCity}.</p>;
                                        }

                                        const selectedDateData = batch.available_dates.find((d) => d.id === selectedDate?.id);
                                        if (!selectedDateData || selectedDateData.mode_of_transports.length === 0) {
                                            return <p>No transport options available for the selected date.</p>;
                                        }

                                        return selectedDateData.mode_of_transports.map((option, index) => {
                                            const selectedOption = selectedTransportOptions.find((op) => op.id === option.id);

                                            return (
                                                <div
                                                    key={index}
                                                    className="grid grid-cols-3 py-1.5 px-3 border border-secondary-text/30 rounded-md"
                                                >
                                                    <span>{option.transport}</span>
                                                    <span className="text-center">
                                                        {selectedDatePrice
                                                            ? `₹${currencyHelper(selectedDatePrice + option.price_per_person)}/-`
                                                            : "Please select a date"}
                                                    </span>
                                                    <div className="flex items-center justify-end">
                                                        <button
                                                            onClick={() => handleTransportPeopleChange("decrement", option)}
                                                            className="text-zinc-400"
                                                        >
                                                            <MinusCircle strokeWidth={1} className="size-4 md:size-5" />
                                                        </button>
                                                        <span className="mx-2">{selectedOption?.people ?? 0}</span>
                                                        <button
                                                            onClick={() => handleTransportPeopleChange("increment", option)}
                                                            className="text-zinc-400"
                                                        >
                                                            <PlusCircle strokeWidth={1} className="size-4 md:size-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>

                            {/* Add-ons */}
                            <div className="border border-secondary-text/40 rounded-lg relative overflow-hidden">
                                <div className="py-2 px-4 md:px-8 text-sm md:text-base grid grid-cols-3 bg-custom-primary-shade w-full">
                                    <p>Add On Options</p>
                                    <p className="text-center">Price(per person)</p>
                                    <div className="flex justify-end">
                                        <User2 className="size-5 md:size-6" />
                                    </div>
                                </div>
                                <div className="p-3 md:p-4 space-y-5 text-sm md:text-base">
                                    {ParsedAddons.length > 0 ? (
                                        ParsedAddons.map((addon, index) => (
                                            <div
                                                key={index}
                                                className="grid grid-cols-3 py-1.5 px-3 border border-secondary-text/30 rounded-md"
                                            >
                                                <span>{addon.title}</span>
                                                <span className="text-center">
                                                    ₹{currencyHelper(parseInt(addon.price))}/-
                                                </span>
                                                <div className="flex items-center justify-end">
                                                    <button
                                                        onClick={() =>
                                                            handleAddonChange("decrement", addon)
                                                        }
                                                        className="text-zinc-400"
                                                    >
                                                        <MinusCircle
                                                            strokeWidth={1}
                                                            className="size-4 md:size-5"
                                                        />
                                                    </button>
                                                    <span className="mx-2">
                                                        {selectedAddons[addon.title] ?? 0}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            handleAddonChange("increment", addon)
                                                        }
                                                        className="text-zinc-400"
                                                    >
                                                        <PlusCircle
                                                            strokeWidth={1}
                                                            className="size-4 md:size-5"
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p>No Add On options available from {selectedCity}.</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-between items-center">
                                <button
                                    onClick={() => setCurrentForm(currentForm - 1)}
                                    className="disabled:cursor-not-allowed disabled:opacity-55 primary-btn bg-gray-800"
                                >
                                    Step Back
                                </button>
                                <button
                                    disabled={totalPeopleForTransport <= 0}
                                    onClick={() => setCurrentForm(currentForm + 1)}
                                    className="disabled:cursor-not-allowed disabled:opacity-55 primary-btn bg-custom-primary"
                                >
                                    Next Step
                                </button>
                            </div>
                        </div>

                        {/* Total Price */}
                        <div className="w-full shrink-0 p-6 lg:p-10 space-y-8">
                            <div>
                                <Label>Name</Label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <PhoneInput
                                    placeholder="phone number"
                                    value={phone ?? ""}
                                    onChange={(value) => setPhone(value)} // Handle value directly
                                    defaultCountry="IN"
                                    className="h-[2.75rem] peer"
                                />
                            </div>
                            <div className="flex justify-between items-center">
                                <button
                                    onClick={() => setCurrentForm(currentForm - 1)}
                                    className="disabled:cursor-not-allowed disabled:opacity-55 primary-btn bg-gray-800 cursor-pointer"
                                >
                                    Step Back
                                </button>
                                <button
                                    disabled={isPending}
                                    onClick={handleBooking}
                                    className="disabled:cursor-not-allowed disabled:opacity-55 primary-btn bg-custom-primary flex items-start"
                                >
                                    {isPending && <Loader2 className="size-5 mr-2 animate-spin" />} Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </>
    )
}